import { IDataRow } from "Entities";
import { EncodingTypes } from "Enums";
import { saveAs } from "file-saver";
import { FormatDefinition } from "Service/Client";
import { de, enUS, Locale } from "date-fns/locale";
import { differenceInDays, formatDistanceStrict } from "date-fns";
import { AppContext } from "./AppContext";
import { Configuration } from "Configuration/Configuration";

/**
 * ## Utility
 *
 * Utility functions to use in DMS.
 *
 * Functionality:
 * - Parse string values to number, date, etc...
 * - Encode / decode strings for different encoding types.
 * - String manipluation like trim, etc...
 * @since 4.0.0
 */
export class Util {
    private static base64KeyStr: string = "ABCDEFGHIJKLMNOP" + "QRSTUVWXYZabcdef" + "ghijklmnopqrstuv" + "wxyz0123456789+/" + "=";
    /**
     * Resolve value to process replacements.
     * @param input Input string.
     * @param dataItem IDataRow with holds the data.
     * @hidden
     */
    public static resolve(input: string, dataItem: IDataRow): string {
        if (input) {
            if (dataItem == null) {
                // null value causes error in template
                dataItem = {};
            }
            return kendo.template(input, { useWithBlock: false })(dataItem);
        }
        return input;
    }

    /**
     * Parse string to result type.
     * @param type Available values: string | number | date.
     * @param value String value to be parsed.
     * @param culture Culture to be used.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // parse number:
     * DMS.Util.parse("number", "12345");
     *
     * // parse date
     * DMS.Util.parse("date", "2017-01-01", "en-US");
     * ```
     */
    public static parse(type: string, value: string, culture?: string): string | number | Date | boolean {
        if (value == "" || value == null || value == "null") {
            return null;
        }

        if (type == "number") {
            return this.parseNumber(value, culture);
        } else if (type == "date") {
            return this.parseDate(value, culture);
        } else if (type == "boolean") {
            return this.parseBoolean(value, culture);
        } else {
            return value;
        }
    }

    /**
     * Parses a string to number.
     * @param value String value to be parsed.
     * @param culture Culture to be used.
     * @since 4.0.0
     */
    public static parseNumber(value: string, culture?: string): number {
        return kendo.parseFloat(value, culture);
    }

    /**
     * Parses a string to boolean.
     * @param value String value to be parsed.
     * @param culture Culture to be used.
     * @since 4.0.0
     */
    public static parseBoolean(value: string, culture?: string): boolean {
        return value == "true" || value == "1" || value == "True";
    }

    /**
     * Parses a string to Date..
     * @param value String value to be parsed.
     * @param culture Culture to be used.
     * @since 4.0.0
     */
    public static parseDate(value: string, culture?: string): Date {
        return kendo.parseDate(value, "", culture);
    }

    /**
     * Convert a value to the correct string representation.
     * Number and dates are formatted according the configured formats.
     * @param value Value to be converted.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // convert number
     * DMS.Util.convertToString(1234.45); // -> "1234,45" in german language format.
     *
     * // convert date
     * DMS.Util.convertToString(new Date()); // -> "01.01.2017 00:00" in german date format.
     * ```
     */
    public static convertToString(value: Date | number | string): string {
        if (value instanceof Date) {
            return kendo.toString(value, AppContext.current.environment.DateTimeFormat);
        } else if (typeof value === "number") {
            return kendo.toString(value, "n2");
        } else {
            return value;
        }
    }

    /**
     * Check if string contains only of digits.
     * @param value Value to be checked.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // check number
     * DMS.Util.isNumber("123"); // -> returns true
     * ```
     */
    public static isNumber(value: string): boolean {
        return /^\d+$/.test(value);
    }

    /**
     * Encode a value.
     * @param value Value to be encoded.
     * @param encodingType Type of the encoding.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // convert to html
     * DMS.Util.encode("&lt;br /&gt;", DMS.Enums.EncodingTypes.Html); // -> "&amp;lt;br /&amp;gt;"
     * ```
     */
    public static encode(value: string, encodingType: EncodingTypes): string {
        let returnValue: string = "";
        switch (encodingType) {
            case EncodingTypes.Url:
                returnValue = encodeURIComponent(value);
                break;
            case EncodingTypes.Html:
                returnValue = $("<div/>").text(value).html();
                break;
            case EncodingTypes.HtmlAttribute:
                returnValue = this.quoteattr(value, true);
                break;
            case EncodingTypes.Javascript:
                returnValue = value.split("'").join("'");
                break;
            case EncodingTypes.Base64:
                value = encodeURI(value);
                let chr1: number, chr2: number, chr3: number;
                let enc1: number, enc2: number, enc3: number, enc4: number;
                let i = 0;

                do {
                    chr1 = value.charCodeAt(i++);
                    chr2 = value.charCodeAt(i++);
                    chr3 = value.charCodeAt(i++);

                    enc1 = chr1 >> 2;
                    enc2 = ((chr1 & 3) << 4) | (chr2 >> 4);
                    enc3 = ((chr2 & 15) << 2) | (chr3 >> 6);
                    enc4 = chr3 & 63;

                    if (isNaN(chr2)) {
                        enc3 = enc4 = 64;
                    } else if (isNaN(chr3)) {
                        enc4 = 64;
                    }
                    returnValue =
                        returnValue +
                        Util.base64KeyStr.charAt(enc1) +
                        Util.base64KeyStr.charAt(enc2) +
                        Util.base64KeyStr.charAt(enc3) +
                        Util.base64KeyStr.charAt(enc4);
                } while (i < value.length);
                break;
        }
        return returnValue;
    }

    /**
     * Decode a value.
     * @param value Value to be decoded.
     * @param encodingType Type of the encoding.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * // convert to text
     * DMS.Util.decode("&amp;lt;br /&amp;gt;", DMS.Enums.EncodingTypes.Html); // -> "&lt;br /&gt;"
     * ```
     */
    public static decode(value: string, encodingType: EncodingTypes): string {
        let returnValue: string = "";
        switch (encodingType) {
            case EncodingTypes.Url:
                returnValue = decodeURIComponent(value);
                break;
            case EncodingTypes.Html:
                returnValue = $("<div/>").html(value).text();
                break;
            case EncodingTypes.HtmlAttribute:
                returnValue = this.unquoteattr(value);
                break;
            case EncodingTypes.Javascript:
                returnValue = value.split("'").join("'");
                break;
            case EncodingTypes.Base64:
                let chr1: number, chr2: number, chr3: number;
                let enc1: number, enc2: number, enc3: number, enc4: number;
                let i = 0;

                value = value.replace(/@@EQUALSIGN@@/g, "=");

                // remove all characters that are not A-Z, a-z, 0-9, +, /, or =
                let base64test = /[^A-Za-z0-9\+\/\=]/g;
                if (base64test.exec(value)) {
                    alert(
                        "There were invalid base64 characters in the input text.\n" +
                            "Valid base64 characters are A-Z, a-z, 0-9, '+', '/',and '='\n" +
                            "Expect errors in decoding."
                    );
                }
                value = value.replace(/[^A-Za-z0-9\+\/\=]/g, "");

                do {
                    enc1 = Util.base64KeyStr.indexOf(value.charAt(i++));
                    enc2 = Util.base64KeyStr.indexOf(value.charAt(i++));
                    enc3 = Util.base64KeyStr.indexOf(value.charAt(i++));
                    enc4 = Util.base64KeyStr.indexOf(value.charAt(i++));

                    chr1 = (enc1 << 2) | (enc2 >> 4);
                    chr2 = ((enc2 & 15) << 4) | (enc3 >> 2);
                    chr3 = ((enc3 & 3) << 6) | enc4;

                    returnValue = returnValue + String.fromCharCode(chr1);

                    if (enc3 != 64) {
                        returnValue = returnValue + String.fromCharCode(chr2);
                    }
                    if (enc4 != 64) {
                        returnValue = returnValue + String.fromCharCode(chr3);
                    }
                } while (i < value.length);

                returnValue = decodeURI(returnValue);
                break;
        }
        return returnValue;
    }

    /**
     * Returns CssClasses for each row
     * @param row
     * @param formatDefinitions
     * @hidden
     */
    public static getFormatCondition(row: kendo.data.Model, formatDefinitions: FormatDefinition[]) {
        let cssClass = "";
        for (let i = 0, len = formatDefinitions.length; i < len; i++) {
            let formatDefinition = formatDefinitions[i];
            if (
                Util.isValueInRange(
                    row.get(formatDefinition.ColumnName),
                    formatDefinition.RangeFrom,
                    formatDefinition.RangeTo,
                    formatDefinition.Compare
                )
            ) {
                cssClass += formatDefinition.CssClass + " ";
            }
        }
        return cssClass;
    }

    /**
     * Checks if Entries(value) should be Visible, checks after rangefrom to RangeTo and compare
     * @param value that should be compared
     * @param rangeFrom
     * @param rangeTo
     * @param compare value that gets compared with compare
     * @hidden
     */
    public static isValueInRange(value: any, rangeFrom?: number, rangeTo?: number, compare?: string, isInverted: boolean = false): boolean {
        let isValid: boolean = true;
        if (!isInverted) {
            if (value != null) {
                if (rangeFrom != null) {
                    isValid = isValid && value >= rangeFrom;
                }
                if (rangeTo != null) {
                    isValid = isValid && value <= rangeTo;
                }
            } else {
                isValid = false;
            }
        } else {
            if (value != null) {
                isValid = false;
                if (rangeFrom != null) {
                    isValid = isValid || value < rangeFrom;
                }
                if (rangeTo != null) {
                    isValid = isValid || value > rangeTo;
                }
            }
        }
        if (compare != null) {
            if (value != compare) {
                isValid = false;
            }
        }
        return isValid;
    }

    /**
     * Removes both trailing and leading occurences of a character in a string.
     * @param str String to be trimmed.
     * @param char Character which will be removed.
     * @since 4.0.0
     */
    public static trim(str: string, char: string = " ") {
        return this.trimStart(this.trimEnd(str, char), char);
    }

    /**
     * Removes all trailing occurences of a character in a string.
     * @param str String to be trimmed.
     * @param char Character which will be removed.
     * @since 4.0.0
     */
    public static trimEnd(str: string, char: string = " "): string {
        if (this.stringIsNullOrEmpty(str)) {
            return null;
        }
        return str.replace(new RegExp("(?:" + this.getRegexEscapedCharacter(char) + ")+$", "g"), "");
    }

    /**
     * Removes all leading occurrences of a character in a string.
     * @param str String to be trimmed.
     * @param char Character which will be removed.
     * @since 4.0.0
     */
    public static trimStart(str: string, char: string = " ") {
        if (this.stringIsNullOrEmpty(str)) {
            return null;
        }
        return str.replace(new RegExp("^(?:" + this.getRegexEscapedCharacter(char) + ")+", "g"), "");
    }

    /**
     * Returns true if the string is null or an empty string.
     * @param str String to be checked.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * DMS.Util.stringIsNullOrEmpty(""); // -> true
     * ```
     */
    public static stringIsNullOrEmpty(str: string): boolean {
        if (str === "" || str == null || !str) {
            return true;
        }
        return false;
    }

    /**
     * Remove html code of a string and return the plain text.
     * @param str String containing html code.
     * @since 4.0.0
     */
    public static stripHTML(str: string) {
        return $("<div/>").html(str).text();
    }

    private static getRegexEscapedCharacter(character: string) {
        return character.replace(/[.\^\$\*\+\?\(\)\[\{\\\|\^\-\]\\\/]/g, "\\$0");
    }

    private static quoteattr(s: string, preserveCR: boolean = false): string {
        const preserveCRstr = preserveCR ? "&#13;" : "\n";
        return ("" + s) /* Forces the conversion to string. */
            .replace(/&/g, "&amp;") /* This MUST be the 1st replacement. */
            .replace(/'/g, "&apos;") /* The 4 other predefined entities, required. */
            .replace(/"/g, "&quot;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/\r\n/g, preserveCRstr) /* Must be before the next replacement. */
            .replace(/[\r\n]/g, preserveCRstr);
    }

    private static unquoteattr(s: string): string {
        s = "" + s; /* Forces the conversion to string type. */
        /* Decode by reversing the initial order of replacements. */
        s = s
            .replace(/\r\n/g, "\n") /* To do before the next replacement. */
            .replace(/[\r\n]/, "\n")
            .replace(/&#13;&#10;/g, "\n") /* These 3 replacements keep whitespaces. */
            .replace(/&#1[03];/g, "\n")
            .replace(/&#9;/g, "\t")
            .replace(/&gt;/g, ">") /* The 4 other predefined entities required. */
            .replace(/&lt;/g, "<")
            .replace(/&quot;/g, "\"")
            .replace(/&apos;/g, "'");

        // const found = /(&[^;])*;?/.exec(s);
        // if (found.length > 0 && found[0] != "&amp;") {
        //     throw "unsafe entity found in the attribute literal content";
        // }
        /* This MUST be the last replacement. */
        s = s.replace(/&amp;/g, "&");
        return s;
    }

    /**
     * @hidden
     */
    public static getNumberFormat(groupSeparator: boolean, decimalDigits: number, displayAsPercent: boolean): string {
        let format: string = "#" + (groupSeparator ? "," : "") + "##0";

        if (decimalDigits > 0) {
            format += ".";
            for (let i = 0; i < decimalDigits; i++) {
                format += "0";
            }
        }
        if (displayAsPercent) {
            format += " \\%";
        }
        return format;
    }

    /**
     * Save binary data to the client by showing a download prompt in the browser.
     * @param data Binary data to be downloaded.
     * @param type Mimetype of the file
     * @param fileName Filename to be saved.
     */
    public static saveFile(data: any, type: string, fileName: string) {
        let blob = new Blob([data], { type: type });
        saveAs(blob, fileName);
    }

    private static convertBase64ToBlob(b64Data: string, contentType: string, sliceSize: number = 512): Blob {
        const byteCharacters = atob(b64Data);
        const byteArrays: Uint8Array[] = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        return new Blob(byteArrays, { type: contentType || "" });
    }

    /**
     * @hidden
     */
    public static saveFileBase64(data: string, type: string, fileName: string) {
        let blob = this.convertBase64ToBlob(data, type);
        saveAs(blob, fileName);
    }

    /**
     * @hidden
     */
    public static formatDate(value: Date): string {
        if (value) {
            if (!(value instanceof Date)) {
                value = new Date(value);
            }

            value = kendo.timezone.convert(value, AppContext.current.environment.ClientTimeZone, "Etc/UTC");
            value = new Date(value.getFullYear(), value.getMonth(), value.getDate());
            return kendo.toString(value, AppContext.current.environment.DateFormat);
        }
    }

    /**
     * @hidden
     */
    public static formatDateTimeNoTimeZone(value: Date): string {
        if (value) {
            if (!(value instanceof Date)) {
                value = new Date(value);
            }

            value = kendo.timezone.convert(
                value,
                AppContext.current.environment.ClientTimeZone,
                AppContext.current.environment.ServerTimeZone
            );
            return kendo.toString(value, AppContext.current.environment.DateTimeFormat);
        }
    }

    /**
     * @hidden
     */
    public static formatDateTimeRelative(value: Date, format: string, maxLength: number): string {
        if (value) {
            if (value instanceof Date && !isNaN(Number(value))) {
                const now = Date.now();
                const diff = Math.abs(differenceInDays(value, now));

                if ((maxLength == null || maxLength == undefined || diff > maxLength) && maxLength != -1) {
                    return kendo.toString(value, format);
                } else {
                    let locale: Locale = enUS;
                    if (AppContext.current.environment.LanguageCode == "D") {
                        locale = de;
                    }
                    return formatDistanceStrict(value, Date.now(), {
                        addSuffix: true,
                        locale: locale,
                    });
                }
            }
        }
    }

    /**
     * @hidden
     */
    public static __sanitizeDataRow(row: IDataRow) {
        if (!row) {
            return;
        }
        delete row.dirty;
        delete row.dirtyFields;
        delete row._events;
        delete row.__original;
        delete row._handlers;
        delete row.uid;
    }

    /**
     * Get the initials of the first two words in a string, using only letters.
     * @param input Input string.
     * @returns Initials in uppercase.
     * @example
     * ```JavaScript
     * Util.getInitials("John Doe"); // -> "JD"
     * Util.getInitials("Martin (Azure AD)"); // -> "MA"
     * Util.getInitials("Single"); // -> "S"
     * ```
     */
    public static getInitials(input: string): string {
        if (!input || typeof input !== "string") {
            return "";
        }
        const words = input.trim().split(/\s+/);
        const initials = words
            .filter((word) => /^[a-zA-Z]/.test(word)) // Only consider words starting with letters
            .slice(0, 2)
            .map((word) => word.charAt(0).toUpperCase())
            .join("");
        return initials;
    }

    /**
     * Copies the input string to the clipboard using the Clipboard API.
     * @param input The string to be copied to the clipboard.
     * @example
     * ```JavaScript
     * Util.copyToClipboard("Hello, World!");
     * ```
     */
    public static async copyToClipboard(input: string): Promise<void> {
        if (!input || typeof input !== "string") {
            return;
        }
        try {
            await navigator.clipboard.writeText(input);
        } catch (err) {
            console.error("Failed to copy text to clipboard:", err);
        }
    }

    public static getLocalStorageItem(key: string): string | null {
        if (Configuration.isDmsConfig) {
            key = "dms-config-" + key;
        }

        return localStorage.getItem(key);
    }

    public static setLocalStorageItem(key: string, value: string): void {
        if (Configuration.isDmsConfig) {
            key = "dms-config-" + key;
        }

        localStorage.setItem(key, value);
    }
}
